import { Directive, DestroyRef, Input, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserService } from '../services/user.service';
import { AccessRights } from '@app/models/user';

@Directive({
	selector: '[hasAccess]',
	standalone: true
})
export class HasAccessDirective implements OnInit {
	@Input() hasAccess: AccessRights;

	private hasView = false;

	constructor(
		private userService: UserService,
		private templateRef: TemplateRef<any>,
		private viewContainer: ViewContainerRef,
		private destroyRef: DestroyRef
	) {}

	/**
	 * Reactive (NOT a one-time `@Input` setter check) — see cognito-to-msal-migration skill
	 * lesson #19/#20: this directive can be evaluated by Angular's change detection BEFORE
	 * `AppComponent`'s async `setUserFromMsal()` call resolves. The old one-time setter called
	 * `userService.hasAccess()`, which reads `this.getUser().access` with NO null-safety — when
	 * `getUser()` is still `null`, this THROWS. Because Angular only re-invokes an `@Input` setter
	 * when the bound expression's VALUE changes (never, for a constant enum reference like
	 * `ACCESS_RIGHTS.VIEW_REGISTRATION_OVERVIEW`), a directive that happened to be evaluated
	 * during that null-user race was PERMANENTLY stuck without its embedded view — even after the
	 * user became available a moment later — while sibling `*hasAccess` directives further down
	 * the same template, whose first evaluation attempt landed on a LATER change-detection tick
	 * (by which time the user was populated), worked fine. This selective, hard-to-reproduce
	 * pattern (some `*hasAccess`-gated nav items missing, others not) is exactly what was observed
	 * on `ip-be-regstar-front`'s left nav (`Registration` group missing, `Move`/`Product Services`
	 * groups fine) even though `UserService.createAccessBy()` always grants
	 * `VIEW_REGISTRATION_OVERVIEW` alongside `MOVE_VIEW`/`PRODUCT_SERVICES_VIEW` for every role.
	 */
	ngOnInit(): void {
		this.userService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
			const granted = !!user && user.access.includes(this.hasAccess);
			if (granted && !this.hasView) {
				this.viewContainer.createEmbeddedView(this.templateRef);
				this.hasView = true;
			} else if (!granted && this.hasView) {
				this.viewContainer.clear();
				this.hasView = false;
			}
		});
	}
}
